/**
 * AuthShell: centered card layout for the auth pages.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useTenantStore } from '../stores/tenant';

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: ReactNode;
}): JSX.Element {
  const { t } = useTranslation();
  const tenant = useTenantStore((s) => s.tenant);
  return (
    <div className="mx-auto max-w-md py-4 sm:py-8">
      <div className="card overflow-hidden">
        <div className="px-7 pb-2 pt-7">
          {tenant && <p className="panel-title text-[color:var(--tenant-primary)]">{tenant.name}</p>}
          <h1 className="mt-1.5 text-2xl font-extrabold tracking-tight text-ink">{title}</h1>
          {subtitle && <p className="mt-1.5 text-sm leading-relaxed text-stone-600">{subtitle}</p>}
        </div>
        <div className="px-7 pb-7 pt-4">{children}</div>
      </div>
      <p className="mt-5 text-center text-sm text-stone-500">
        <Link to="/" className="font-medium hover:text-ink hover:underline">
          {t('auth.backTo', { name: tenant?.name ?? t('auth.home') })}
        </Link>
      </p>
    </div>
  );
}

export function AuthField({
  label,
  ...inputProps
}: { label: string } & React.InputHTMLAttributes<HTMLInputElement>): JSX.Element {
  return (
    <label className="field-label">
      {label}
      <input {...inputProps} className="field-input py-2.5" />
    </label>
  );
}

export function AuthSubmit({
  busy,
  busyLabel,
  children,
}: {
  busy: boolean;
  busyLabel: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="submit"
      disabled={busy}
      className="btn-primary w-full py-2.5"
    >
      {busy ? busyLabel : children}
    </button>
  );
}
